import type { ReactNode, ReactElement } from "react";
import MuiTooltip, { type TooltipClasses } from "@mui/material/Tooltip";

export type Position = "top" | "right" | "bottom" | "left";

export interface TooltipProps {
  /** Any additional classes to pass in */
  classes?: Partial<TooltipClasses>;

  /**
   * What is displayed by the tooltip. Could be a string, HTML, or any existing component.
   */
  content: ReactNode;
  /**
   * The placement of the tooltip content relative to the element being hovered.
   * If there is no room in that orientation, the tooltip automatically shifts to
   * a different placement.
   */
  position?: Position;
  /**
   * If `arrow = true`, the tooltip will have an arrow indicating which element it refers to.
   */
  arrow?: boolean | undefined;

  enterDelay?: number;

  leaveDelay?: number;

  children: ReactElement;
}

/**
 * Tooltip provides an element that can be hovered to reveal a tooltip that contains a message
 * @param iProps TooltipProps
 * @returns JSX element that can be hovered to reveal a tooltip
 */
export const Tooltip = (iProps: TooltipProps) => {
  const {
    content,
    classes,
    position = "right",
    arrow,
    children,
    enterDelay,
    leaveDelay,
  } = iProps;

  return (
    <MuiTooltip
      classes={classes}
      title={content}
      placement={position}
      arrow={arrow}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
    >
      {children}
    </MuiTooltip>
  );
};
